import { BaseControl } from '..';
import { IconButtonGroupControl } from '../IconButtonGroupControl';
import { ImageControl } from '../ImageControl';
import GridOnIcon from '@mui/icons-material/GridOn';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import AppsIcon from '@mui/icons-material/Apps';
import PhotoSizeSelectActualIcon from '@mui/icons-material/PhotoSizeSelectActual';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import NorthWestIcon from '@mui/icons-material/NorthWest';
import NorthIcon from '@mui/icons-material/North';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import WestIcon from '@mui/icons-material/West';
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';
import EastIcon from '@mui/icons-material/East';
import SouthWestIcon from '@mui/icons-material/SouthWest';
import SouthIcon from '@mui/icons-material/South';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import { ColorControl } from '../ColorControl';
import { SliderControl } from '../SliderControl';

/**
 * @typedef {object} BackgroundControlOptions
 */

/**
 * @typedef {object} BackgroundControlValue
 * @property {string} [backgroundColor] - Background color
 * @property {number} [backgroundColorOpacity] - Opacity of the background color in percent (0-100)
 * @property {string} [backgroundImage] - Background image
 * @property {string} [backgroundOverlayColor] - Color painted over the background image ('' = no overlay)
 * @property {number} [backgroundOverlayOpacity] - Opacity of the overlay color in percent (0-100)
 * @property {string} [backgroundRepeat] - Background repeat option
 * @property {string} [backgroundSize] - Background size option
 * @property {string} [backgroundPosition] - Background position option
 */

/**
 * @extends {BaseControl<BackgroundControlValue, BackgroundControlOptions>}
 */
export class BackgroundControl extends BaseControl {
    
    /**
     * @param {import('..').BaseControlDefinition<BackgroundControlValue, BackgroundControlOptions>} control 
     */
    constructor(control) {
        super(control);

        this.children = {
            'backgroundColor': new ColorControl({
                label: 'Achtergrondkleur',
                description: 'Achtergrondkleur',
                defaultValue: '#000000',
                options: {
                    useHousestyles: true
                }
            }),
            'backgroundColorOpacity': new SliderControl({
                label: 'Transparantie',
                description: 'Hoe dekkend de achtergrondkleur is (0 = volledig transparant, 100 = volledig dekkend)',
                defaultValue: 100,
                options: {
                    min: 0,
                    max: 100,
                    step: 1,
                    suffix: '%'
                },
                visibleWhen: (_widget, background) =>
                    Boolean(background?.backgroundColor)
            }),
            'backgroundImage': new ImageControl({
                label: 'Achtergrondafbeelding',
                description: 'Achtergrondafbeelding',
                defaultValue: '',
                options: {}
            }),
            'backgroundOverlayColor': new ColorControl({
                label: 'Overlaykleur',
                description: 'Kleur die over de achtergrondafbeelding wordt gelegd',
                defaultValue: '',
                options: {
                    useHousestyles: true,
                    noneLabel: 'Geen overlay'
                },
                visibleWhen: (_widget, background) =>
                    Boolean(background?.backgroundImage)
            }),
            'backgroundOverlayOpacity': new SliderControl({
                label: 'Overlay dekking',
                description: 'Hoe dekkend de overlaykleur is (0 = onzichtbaar, 100 = volledig dekkend)',
                defaultValue: 50,
                options: {
                    min: 0,
                    max: 100,
                    step: 1,
                    suffix: '%'
                },
                visibleWhen: (_widget, background) =>
                    Boolean(background?.backgroundImage && background?.backgroundOverlayColor)
            }),
            'backgroundRepeat': new IconButtonGroupControl({
                label: 'Achtergrond herhalen',
                description: 'Hoe de achtergrondafbeelding herhaald moet worden',
                defaultValue: 'repeat',
                options: {
                    buttons: [
                        {
                            value: 'repeat',
                            icon: <GridOnIcon />,
                            tooltip: 'Herhalen (alle richtingen)',
                        },
                        {
                            value: 'no-repeat',
                            icon: <CropSquareIcon />,
                            tooltip: 'Niet herhalen',
                        },
                        {
                            value: 'repeat-x',
                            icon: <SyncAltIcon />,
                            tooltip: 'Herhalen (horizontaal)',
                        },
                        {
                            value: 'repeat-y',
                            icon: <SwapVertIcon />,
                            tooltip: 'Herhalen (verticaal)',
                        },
                        // {
                        //     value: 'space',
                        //     icon: <AppsIcon />,
                        //     tooltip: 'Gelijkmatig herhalen',
                        // },
                    ]
                }
            }),
            'backgroundSize': new IconButtonGroupControl({
                label: 'Achtergrond grootte',
                description: 'Hoe de achtergrondafbeelding geschaald moet worden',
                defaultValue: 'auto',
                options: {
                    buttons: [
                        {
                            value: 'auto',
                            icon: <PhotoSizeSelectActualIcon />,
                            tooltip: 'Automatische grootte',
                        },
                        {
                            value: 'cover',
                            icon: <FullscreenIcon />,
                            tooltip: 'Volledig bedekken',
                        },
                        {
                            value: 'contain',
                            icon: <FitScreenIcon />,
                            tooltip: 'Volledig tonen',
                        },
                    ]
                }
            }),
            'backgroundPosition': new IconButtonGroupControl({
                label: 'Achtergrond positie',
                description: 'Positie van de achtergrondafbeelding',
                defaultValue: 'center',
                options: {
                    buttons: [
                        {
                            value: 'left center',
                            icon: <WestIcon />,
                            tooltip: 'Links midden',
                        },
                        {
                            value: 'left top',
                            icon: <NorthWestIcon />,
                            tooltip: 'Links boven',
                        },
                        {
                            value: 'center top',
                            icon: <NorthIcon />,
                            tooltip: 'Midden boven',
                        },
                        {
                            value: 'right top',
                            icon: <NorthEastIcon />,
                            tooltip: 'Rechts boven',
                        },
                        {
                            value: 'center center',
                            icon: <CenterFocusWeakIcon />,
                            tooltip: 'Midden',
                        },
                        {
                            value: 'left bottom',
                            icon: <SouthWestIcon />,
                            tooltip: 'Links onder',
                        },
                        {
                            value: 'center bottom',
                            icon: <SouthIcon />,
                            tooltip: 'Midden onder',
                        },
                        {
                            value: 'right bottom',
                            icon: <SouthEastIcon />,
                            tooltip: 'Rechts onder',
                        },
                        {
                            value: 'right center',
                            icon: <EastIcon />,
                            tooltip: 'Rechts midden',
                        },
                    ]
                }
            }),
        };
    }
}
